import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, 
  HardHat, 
  Ruler, 
  FileText, 
  Users, 
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Landmark,
  ShieldCheck,
  Calculator,
  Home,
  Briefcase,
  BarChart3
} from 'lucide-react';

const ServiciosPage = () => {
  const [activeTab, setActiveTab] = useState('desarrollo');

  const serviciosDesarrollo = [
    {
      icon: Building2,
      title: 'Desarrollo Inmobiliario',
      description: 'Proyectos residenciales, comerciales y de uso mixto con los más altos estándares de calidad.',
      features: ['Residencial vertical', 'Centros comerciales', 'Oficinas corporativas', 'Desarrollos mixtos']
    },
    {
      icon: HardHat,
      title: 'Construcción',
      description: 'Ejecución de obra con supervisión profesional y cumplimiento de normas de construcción.',
      features: ['Obra civil', 'Acabados premium', 'Instalaciones especiales', 'Control de calidad']
    },
    {
      icon: Ruler,
      title: 'Diseño Arquitectónico',
      description: 'Proyectos arquitectónicos innovadores que maximizan el valor del inmueble.',
      features: ['Diseño conceptual', 'Renders 3D', 'Planos ejecutivos', 'Interiorismo']
    },
    {
      icon: FileText,
      title: 'Gestión de Permisos',
      description: 'Tramitología completa ante autoridades municipales y federales.',
      features: ['Licencias de construcción', 'Uso de suelo', 'Impacto ambiental', 'Régimen condominal']
    }
  ];

  const serviciosInversion = [
    {
      icon: TrendingUp,
      title: 'Inversión en Proyectos',
      description: 'Participa en desarrollos inmobiliarios de alto rendimiento con capital protegido.',
      features: ['Rendimientos proyectados 12-18% anual', 'Capital garantizado', 'Reportes trimestrales', 'Salida programada']
    },
    {
      icon: Landmark,
      title: 'Fideicomisos de Inversión',
      description: 'Estructuras legales que protegen tu inversión con gobernanza corporativa.',
      features: ['Fideicomiso de garantía', 'Fideicomiso de administración', 'Transparencia total', 'Auditoría externa']
    },
    {
      icon: ShieldCheck,
      title: 'Due Diligence',
      description: 'Análisis exhaustivo de cada oportunidad antes de ser presentada a inversionistas.',
      features: ['Análisis legal', 'Estudio de mercado', 'Viabilidad financiera', 'Evaluación de riesgos']
    },
    {
      icon: Calculator,
      title: 'Estructuración Financiera',
      description: 'Diseño de esquemas de inversión optimizados fiscalmente.',
      features: ['Optimización fiscal', 'Flujos de caja', 'Modelos financieros', 'Estrategias de salida']
    }
  ];

  const serviciosAsesoria = [
    {
      icon: Briefcase,
      title: 'Consultoría Inmobiliaria',
      description: 'Asesoría estratégica para maximizar el valor de tu patrimonio inmobiliario.',
      features: ['Valuación de activos', 'Estrategia de portafolio', 'Análisis de mercado', 'Recomendaciones de inversión']
    },
    {
      icon: Home,
      title: 'Administración de Propiedades',
      description: 'Gestión integral de inmuebles para maximizar su rentabilidad.',
      features: ['Cobranza de rentas', 'Mantenimiento preventivo', 'Relación con inquilinos', 'Reportes mensuales']
    },
    {
      icon: Users,
      title: 'Family Office Inmobiliario',
      description: 'Servicio personalizado para familias con patrimonio inmobiliario significativo.',
      features: ['Estrategia patrimonial', 'Sucesión ordenada', 'Diversificación', 'Asesoría fiscal']
    },
    {
      icon: BarChart3,
      title: 'Reportes e Inteligencia',
      description: 'Información de mercado y análisis para toma de decisiones informadas.',
      features: ['Reportes de mercado', 'Benchmarking', 'Tendencias sectoriales', 'Análisis comparativo']
    }
  ];

  const tabs = [
    { id: 'desarrollo', label: 'Desarrollo', icon: Building2 },
    { id: 'inversion', label: 'Inversión', icon: TrendingUp },
    { id: 'asesoria', label: 'Asesoría', icon: Briefcase }
  ];

  const getActiveServices = () => {
    switch (activeTab) {
      case 'desarrollo': return serviciosDesarrollo;
      case 'inversion': return serviciosInversion;
      case 'asesoria': return serviciosAsesoria;
      default: return serviciosDesarrollo;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-zinc-900 to-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-6 text-xs uppercase tracking-widest">
              Nuestros Servicios
            </Badge>
            <h1 className="heading-display mb-6">
              Soluciones Integrales<br />
              <span className="text-muted-foreground">en Bienes Raíces</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Desde el desarrollo de proyectos hasta la estructuración de inversiones, 
              ofrecemos servicios completos para maximizar el valor de tu patrimonio inmobiliario.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center gap-2 py-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-sm text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {getActiveServices().map((servicio, index) => {
              const Icon = servicio.icon;
              return (
                <Card key={index} className="card-dashboard group hover:border-primary/30 transition-all">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-sm bg-primary/10 text-primary">
                        <Icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">{servicio.title}</CardTitle>
                        <CardDescription className="text-sm leading-relaxed">
                          {servicio.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {servicio.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" strokeWidth={1.5} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="heading-section mb-6">
            ¿Listo para comenzar?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Agenda una consulta sin compromiso con nuestro equipo de especialistas 
            y descubre cómo podemos ayudarte a alcanzar tus objetivos inmobiliarios.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="gap-2">
                Contactar a un Especialista
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/oportunidades">
              <Button size="lg" variant="outline" className="gap-2">
                Ver Oportunidades de Inversión
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiciosPage;
